import { Clock, Zap } from 'lucide-react'
import type React from 'react'
import { useMemo } from 'react'
import { formatISTTime } from '../../lib/date-utils'
import { useMarketStore } from '../../services/store'
import { isMarketHours } from '../../config/constants'

export const LiveTickerDisplay: React.FC = () => {
    const timestamp = useMarketStore((state) => state.lastUpdated)
    const latency = useMarketStore((state) => state.liveLatency)
    const liveDataSource = useMarketStore((state) => state.liveDataSource || 'EDGE_API')

    const isMarketOpen = isMarketHours()

    const tierLabel = useMemo(() => {
        if (liveDataSource === 'UPSTOX_PROXY') return { name: 'Tier 2', title: 'Dedicated Server Proxy', style: 'text-sky-600 dark:text-sky-400 bg-sky-500/10 border-sky-400/30' }
        if (liveDataSource === 'LIVE_CSV_FALLBACK') return { name: 'Tier 3', title: 'Google Sheets Live CSV', style: 'text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-400/30' }
        return { name: 'Tier 1', title: 'Cloudflare Edge API', style: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-400/30' }
    }, [liveDataSource])

    const displayTime = useMemo(() => {
        if (!timestamp) return '--:--:--'
        return formatISTTime(timestamp)
    }, [timestamp])

    // Latency text color coding
    const latencyTextColor = latency <= 150 
        ? 'text-emerald-700 dark:text-emerald-400'
        : latency <= 350
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-rose-700 dark:text-rose-400'

    return (
        <div 
            title={`Live Feed: ${tierLabel.name} (${tierLabel.title}) | Latency: ${latency > 0 ? `${latency}ms` : 'Connecting...'} (${isMarketOpen ? '2-Second Market Cadence' : '1-Minute Off-Market Cadence'})`}
            className="flex items-center gap-2 px-3 py-1 bg-emerald-50/90 dark:bg-emerald-950/40 rounded-full shadow-xs border border-emerald-200/60 dark:border-emerald-800/50 shrink-0 select-none group hover:bg-emerald-100/80 transition-colors"
        >
            {/* Clock Section */}
            <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <div
                    className={`w-1.5 h-1.5 rounded-full ${isMarketOpen ? 'bg-emerald-500' : 'bg-slate-400'} ${timestamp ? 'animate-pulse' : ''}`}
                />
                <span className="premium-cell-mono font-bold text-emerald-900 dark:text-emerald-100 text-[11px] md:text-[12px] tracking-tight">
                    {displayTime}
                </span>
            </div>

            {/* Source Tier Badge */}
            <span 
                className={`px-1.5 py-0.2 rounded-full border text-[8px] font-black uppercase tracking-tight ${tierLabel.style}`}
                title={`Live Data Source: ${tierLabel.name} (${tierLabel.title})`}
            >
                {tierLabel.name}
            </span>

            {/* Subtle Divider */}
            <div className="w-px h-3.5 bg-emerald-300/60 dark:bg-emerald-700/60" />

            {/* Compact Latency & Cadence Badge */}
            <div className={`flex items-center gap-1 text-[10px] md:text-[11px] font-extrabold ${latencyTextColor}`}>
                <Zap className="w-3 h-3 fill-current shrink-0" />
                <span className="tabular-nums font-mono">{latency > 0 ? `${latency}ms` : 'Live'}</span>
                <span className="hidden xl:inline text-[9px] font-bold opacity-75">
                    • {isMarketOpen ? '2s' : '1m'}
                </span>
            </div>
        </div>
    )
}

export default LiveTickerDisplay
